import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SEO from "../components/SEO";
import { Reveal } from "../components/Reveal";
import CTABlock from "../components/CTABlock";
import WhatsAppIcon from "../components/WhatsAppIcon";
import { SEO_META, CATEGORIES, GARMENT_TYPES, waUrl, waCategoryMsg } from "../config/site";

function GarmentTypes() {
  return (
    <section className="border-t border-obsidian/10 bg-white" data-testid="garment-types-section">
      <div className="container-site py-16 lg:py-24">
        <Reveal>
          <p className="eyebrow">By Garment Type</p>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold leading-[1.08] tracking-tight sm:text-4xl">
            Every garment your team wears, from one manufacturer
          </h2>
          <p className="mt-4 max-w-xl text-base text-obsidian/60">
            Source individual garment types or combine them into a complete uniform program — all customized
            to your fabric, colors, fit and branding.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-px border border-obsidian/10 bg-obsidian/10 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {GARMENT_TYPES.map((g, i) => (
            <Reveal key={g.slug} delay={(i % 4) * 0.06} className="bg-white">
              <div className="group flex h-full flex-col p-7 transition-colors duration-300 hover:bg-ivory" data-testid={`garment-card-${g.slug}`}>
                <span className="font-mono text-xs font-semibold tracking-[0.2em] text-cleef">{String(i + 1).padStart(2, "0")}</span>
                <Link to={`/uniforms/${g.slug}`} className="mt-3 block font-display text-lg font-bold leading-tight tracking-tight transition-colors hover:text-cleef">
                  {g.name}
                </Link>
                <p className="mt-2 text-sm leading-relaxed text-obsidian/60">{g.line}</p>
                <ul className="mt-4 flex flex-wrap gap-1.5">
                  {g.items.map((item) => (
                    <li key={item} className="border border-obsidian/10 bg-ivory px-2.5 py-1 text-[11px] font-medium text-obsidian/65">
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="mt-auto flex items-center gap-4 pt-6">
                  <Link
                    to={`/uniforms/${g.slug}`}
                    data-testid={`garment-view-btn-${g.slug}`}
                    className="inline-flex items-center gap-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-obsidian transition-colors hover:text-cleef"
                  >
                    View Details <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                  <a
                    href={waUrl(waCategoryMsg(g.name.toLowerCase()))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-event="whatsapp_click"
                    data-testid={`garment-whatsapp-btn-${g.slug}`}
                    aria-label={`Ask about ${g.name} on WhatsApp`}
                    className="text-obsidian/50 transition-colors hover:text-[#128C7E]"
                  >
                    <WhatsAppIcon className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Uniforms() {
  const meta = SEO_META.uniforms;
  return (
    <>
      <SEO title={meta.title} description={meta.description} path={meta.path} />
      <section className="border-b border-obsidian/10 bg-bone" data-testid="uniforms-hero">
        <div className="container-site py-16 lg:py-24">
          <Reveal>
            <p className="eyebrow">Uniform Categories</p>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl">
              Designed for the job. Built for the team.
            </h1>
            <p className="mt-5 max-w-xl text-base leading-relaxed text-obsidian/60">
              Bulk uniform programs across eight categories — every style developed to your specification,
              fabric and branding. No fixed retail pricing: quotations are built around your requirement.
            </p>
          </Reveal>
        </div>
      </section>

      <div className="container-site py-16 lg:py-24">
        {CATEGORIES.map((cat, i) => (
          <section
            key={cat.slug}
            id={cat.slug}
            data-testid={`category-section-${cat.slug}`}
            className="grid items-center gap-10 border-b border-obsidian/10 py-14 first:pt-0 last:border-0 lg:grid-cols-2 lg:gap-16"
          >
            <Reveal className={i % 2 ? "lg:order-2" : ""}>
              <div className="frame-offset overflow-hidden">
                <img src={cat.img} alt={cat.alt} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-[1.04]" />
              </div>
            </Reveal>
            <Reveal delay={0.1} className={i % 2 ? "lg:order-1" : ""}>
              <span className="font-mono text-xs font-semibold tracking-[0.2em] text-cleef">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight sm:text-3xl">{cat.name}</h2>
              <p className="mt-3 max-w-md text-base leading-relaxed text-obsidian/60">{cat.line}</p>
              <ul className="mt-6 flex flex-wrap gap-2">
                {cat.products.map((p) => (
                  <li key={p} className="border border-obsidian/15 bg-white px-3.5 py-1.5 text-xs font-medium text-obsidian/75">
                    {p}
                  </li>
                ))}
              </ul>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/bulk-quote" data-event="bulk_quote_click" data-testid={`category-quote-btn-${cat.slug}`} className="btn-primary">
                  Request Bulk Quote <ArrowRight className="h-4 w-4" />
                </Link>
                <a
                  href={waUrl(waCategoryMsg(cat.name.toLowerCase()))}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-event="whatsapp_click"
                  data-testid={`category-whatsapp-btn-${cat.slug}`}
                  className="btn-outline"
                >
                  <WhatsAppIcon className="h-4 w-4 text-[#25D366]" /> WhatsApp
                </a>
              </div>
            </Reveal>
          </section>
        ))}
      </div>
      <GarmentTypes />
      <CTABlock />
    </>
  );
}
